/**
 * AttachmentsThumbnailPopover
 * ---------------------------
 * Popover (HoverCard) qui liste toutes les pièces jointes d'un document
 * sous forme de miniatures 40×40 (border-radius 4px). Ouvert au survol
 * du compteur « 📎 N » dans le tableau Documents.
 *
 * Sources de fichiers :
 *   1. Champs dynamiques du document (`doc.values`) — dict legacy OU
 *      liste (multi-upload). Endpoint :
 *        GET /documents/{doc_id}/field-file/{field_key}?idx={idx}
 *   2. Fichiers d'étapes (`doc.steps[*].field_filenames`). Endpoint :
 *        GET /documents/{doc_id}/steps/{step_instance_id}/file?field_id={field_id}
 *
 * Comportement lazy : les blobs d'images ne sont téléchargés QU'à
 * l'ouverture du popover (via `onOpenChange`) pour éviter de matraquer
 * B2 quand l'utilisateur scrolle. Les Object URLs sont révoquées à
 * l'unmount pour libérer la mémoire.
 *
 * Spec : Message #833 (2026-02-XX) — demande utilisateur.
 */
import { useEffect, useRef, useState } from "react";
import { FileText, Paperclip, ExternalLink } from "lucide-react";
import { getFileTypeMeta } from "@/lib/fileTypeMeta";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import api from "@/lib/api";
import { useFilePreview } from "@/context/FilePreviewContext";

/**
 * Extrait TOUTES les pièces jointes d'un document (values + steps) et
 * les retourne dans l'ordre : d'abord les champs dynamiques, puis les
 * fichiers rattachés aux étapes du plus ancien au plus récent.
 *
 * Retour : Array<{
 *   key: string,                 // clé unique pour React
 *   filename: string,
 *   contentType: string,
 *   downloadUrl: string,         // relative to api baseURL
 *   sourceLabel: string,         // ex. "Champ · Facture" ou "Étape 2 · Devis"
 * }>
 */
export function collectAttachments(doc, docType) {
  const out = [];
  const fieldsByKey = {};
  (docType?.fields || []).forEach((f) => { fieldsByKey[f.key] = f; });

  // --- 1. Champs dynamiques ---------------------------------------------
  const values = doc.values || {};
  Object.entries(values).forEach(([fieldKey, v]) => {
    const items = Array.isArray(v)
      ? v.filter((x) => x && typeof x === "object" && x.original_filename)
      : (v && typeof v === "object" && v.original_filename ? [v] : []);
    items.forEach((file, idx) => {
      out.push({
        key: `field-${fieldKey}-${idx}`,
        filename: file.original_filename,
        contentType: file.content_type || "application/octet-stream",
        downloadUrl: `/documents/${doc.id}/field-file/${encodeURIComponent(fieldKey)}?idx=${idx}`,
        sourceLabel: `Champ · ${fieldsByKey[fieldKey]?.label || fieldKey}`,
      });
    });
  });

  // --- 2. Fichiers d'étapes ---------------------------------------------
  (doc.steps || []).forEach((s, sIdx) => {
    const ff = s.field_filenames || {};
    const cts = s.field_content_types || {};
    const stepLabel = s.label || `Étape ${sIdx + 1}`;
    const snap = s.snap_required_fields || [];
    const snapById = {};
    snap.forEach((f) => { snapById[f.id] = f; });

    const filenameEntries = Object.entries(ff).filter(([, v]) => {
      if (Array.isArray(v)) return v.some(Boolean);
      return Boolean(v);
    });
    if (filenameEntries.length > 0) {
      filenameEntries.forEach(([fieldId, val]) => {
        // Spec 2026-02-XX ext#5 — chaque fichier de la liste devient une
        // miniature indépendante (idx=0..N).
        const list = Array.isArray(val) ? val : [val];
        const ctList = Array.isArray(cts[fieldId]) ? cts[fieldId] : (cts[fieldId] ? [cts[fieldId]] : []);
        list.forEach((fname, fIdx) => {
          if (!fname) return;
          out.push({
            key: `step-${s.id}-${fieldId}-${fIdx}`,
            filename: fname,
            contentType: ctList[fIdx] || "application/octet-stream",
            downloadUrl: `/documents/${doc.id}/steps/${s.id}/file?field_id=${encodeURIComponent(fieldId)}&idx=${fIdx}`,
            sourceLabel: `${stepLabel} · ${snapById[fieldId]?.label || fieldId}`,
          });
        });
      });
    } else if (s.field_filename) {
      // Legacy mirror single-file.
      out.push({
        key: `step-${s.id}-legacy`,
        filename: s.field_filename,
        contentType: s.field_content_type || "application/octet-stream",
        downloadUrl: `/documents/${doc.id}/steps/${s.id}/file`,
        sourceLabel: stepLabel,
      });
    }
  });

  return out;
}

const isImage = (ct) => /^image\//i.test(ct || "");

function Thumbnail({ att, blobUrl, onClick }) {
  const [errored, setErrored] = useState(false);
  const showImage = isImage(att.contentType) && blobUrl && !errored;
  // Spec rev150 (2026-02-07) — badge coloré par type de fichier via
  // le helper partagé `getFileTypeMeta`. Rendu inline dans la
  // miniature 40×40 : si image + blob dispo → image réelle ; sinon
  // icône typée + couleurs typées (PDF rouge, XLS vert, DOC bleu, …).
  const { Icon, iconColor, bg } = getFileTypeMeta(att.filename);
  return (
    <button
      type="button"
      onClick={(e) => {
        // Radix Portal propage l'événement dans l'arbre React → sans
        // stopPropagation le onClick de la ligne (navigate → /documents/:id)
        // se déclencherait derrière la modale. Spec 2026-02-XX rev34.
        e.stopPropagation();
        onClick?.(e);
      }}
      title={`${att.filename}\n${att.sourceLabel}`}
      data-testid={`doc-attach-thumb-${att.key}`}
      className="group relative flex items-center justify-center overflow-hidden transition-transform hover:scale-105"
      style={{
        width: 40, height: 40, borderRadius: 8,
        background: bg,
        border: `1px solid ${bg}`,
        color: iconColor,
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      {showImage ? (
        <img
          src={blobUrl}
          alt={att.filename}
          onError={() => setErrored(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", borderRadius: 7 }}
        />
      ) : (
        <Icon size={20} strokeWidth={2} />
      )}
      <span
        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: "rgba(0,0,0,0.55)", borderRadius: 4 }}
      >
        <ExternalLink size={14} className="text-white" />
      </span>
    </button>
  );
}

export default function AttachmentsThumbnailPopover({ doc, docType, count, iconSize = 12 }) {
  const [open, setOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [blobs, setBlobs] = useState({}); // key → objectURL
  const objectUrlsRef = useRef([]);
  // Spec 2026-02-XX rev34 — utilise la visionneuse intégrée globale
  // (FilePreviewContext) au lieu de window.open, pour que l'utilisateur
  // reste dans Le Desk et puisse fermer proprement avec ✕ ou Échap.
  const { openAuthenticatedUrl } = useFilePreview();

  // Prépare la liste des pièces jointes à l'ouverture (calcul rapide).
  useEffect(() => {
    if (open) {
      setAttachments(collectAttachments(doc, docType));
    }
  }, [open, doc, docType]);

  // Charge les blobs images lazy, une seule fois par clé.
  useEffect(() => {
    if (!open || attachments.length === 0) return;
    let cancelled = false;
    attachments
      .filter((a) => isImage(a.contentType) && !blobs[a.key])
      .forEach(async (att) => {
        try {
          const resp = await api.get(att.downloadUrl, { responseType: "blob" });
          if (cancelled) return;
          const url = URL.createObjectURL(resp.data);
          objectUrlsRef.current.push(url);
          setBlobs((prev) => ({ ...prev, [att.key]: url }));
        } catch {
          // Silent — la miniature retombera sur l'icône FileText.
        }
      });
    return () => { cancelled = true; };
  }, [open, attachments, blobs]);

  // Cleanup des Object URLs au démontage.
  useEffect(() => () => {
    objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    objectUrlsRef.current = [];
  }, []);

  // Spec 2026-02-XX rev34 — La visionneuse intégrée gère elle-même les
  // formats non-viewables (message "Aperçu indisponible" + bouton
  // Télécharger). Plus besoin d'un helper local isViewableInline ici.

  const openFile = async (att) => {
    // Spec 2026-02-XX rev34 — délègue à la visionneuse intégrée globale.
    // Elle télécharge via axios (token auto), affiche image/PDF/texte
    // dans une modale plein écran avec bouton Fermer + Télécharger.
    // Referme le popover trombone pour dégager l'écran.
    setOpen(false);
    await openAuthenticatedUrl(att.downloadUrl, {
      filename: att.filename,
      contentType: att.contentType,
    });
  };

  const hasAny = (count || 0) > 0;

  return (
    <HoverCard openDelay={150} closeDelay={80} open={hasAny ? open : false} onOpenChange={hasAny ? setOpen : undefined}>
      <HoverCardTrigger asChild>
        <span
          className="inline-flex items-center gap-1 tabular-nums select-none"
          style={{ color: hasAny ? "#EDEDED" : "#666", cursor: hasAny ? "default" : "default" }}
          data-testid={`doc-attach-trigger-${doc.id}`}
        >
          <Paperclip size={iconSize} />
          {count}
        </span>
      </HoverCardTrigger>
      {hasAny && (
        <HoverCardContent
          align="start"
          side="top"
          className="p-3"
          style={{ width: "auto", maxWidth: 340, background: "#0F0F10", border: "1px solid #2A2A2A" }}
          data-testid={`doc-attach-popover-${doc.id}`}
        >
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            Pièces jointes · {attachments.length}
          </div>
          <div className="flex flex-wrap gap-2" style={{ maxWidth: 316 }}>
            {attachments.map((att) => (
              <Thumbnail
                key={att.key}
                att={att}
                blobUrl={blobs[att.key]}
                onClick={() => openFile(att)}
              />
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground mt-2">
            Cliquez sur une miniature pour l'ouvrir.
          </div>
        </HoverCardContent>
      )}
    </HoverCard>
  );
}
